import { canAdminWrite } from '../auth/AuthContext';
import { ADMIN_NAV_GROUPS, type AdminNavGroup, type AdminNavItem } from './adminPlatformModel';
import { hasCommerceCapability } from './commerceCapabilities';

/** Canonical role/capability predicate shared by every navigation consumer. */
export function navVisible(item: AdminNavItem, role: string | null | undefined): boolean {
  if (item.capability && !hasCommerceCapability(role, item.capability)) return false;
  return !item.write || canAdminWrite(role);
}

/**
 * Groups after canonical filtering + the C7 orphan rule. Sidebar, Dashboard,
 * command palette, AI assistant, and direct-route tests all consume this view.
 */
export function visibleGroups(role: string | null | undefined): AdminNavGroup[] {
  const groups = ADMIN_NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => navVisible(item, role)),
  }));
  const hostExists = (label?: string) =>
    Boolean(label) && groups.some((group) => group.label === label && group.items.length > 0 && group.label !== '');
  const moved: AdminNavItem[] = [];
  const kept = groups.filter((group) => {
    if (
      group.items.length === 1 &&
      group.items[0].fallbackGroup &&
      hostExists(group.items[0].fallbackGroup) &&
      group.items[0].fallbackGroup !== group.label
    ) {
      moved.push(group.items[0]);
      return false;
    }
    return group.items.length > 0;
  });
  for (const item of moved) {
    const host = kept.find((group) => group.label === item.fallbackGroup);
    if (host) host.items.push(item);
  }
  return kept;
}

export function visibleNavItems(role: string | null | undefined): AdminNavItem[] {
  return visibleGroups(role).flatMap((group) => group.items);
}

export function canAccessNavRoute(role: string | null | undefined, route: string): boolean {
  return visibleNavItems(role).some((item) => {
    const paths = [item.to, ...(item.aliases ?? [])];
    return paths.some((path) => path === route || (path !== '/' && route.startsWith(`${path}/`)));
  });
}

